import axios from "axios"

// Create axios instance with base URL from environment variables
const api = axios.create({
  baseURL: "https://api.bharatdarshan.app/api",
  headers: {
    "Content-Type": "application/json",
  },
})

// Add request interceptor to include auth token in headers
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token")
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error),
)

// Add response interceptor to handle common errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      // Only force logout when the failing request is an auth-related endpoint
      // (login/register/refresh/me). Random 401s from other APIs must NOT
      // wipe the session — that caused users to be silently logged out.
      const url = (error.config && (error.config.url || '')) || ''
      const isAuthEndpoint = /\/auth\//i.test(url) || /\/auth$/i.test(url)
      if (isAuthEndpoint) {
        localStorage.removeItem("token")
        // Avoid redirect loop if already on a public page
        if (typeof window !== 'undefined' && !/\/(login|register|^\/$)/i.test(window.location.pathname)) {
          window.location.href = "/"
        }
      }
    }
    return Promise.reject(error)
  },
)

// Auth services
export const authService = {
  register: (userData) => api.post("/auth/register", userData),
  login: (credentials) => api.post("/auth/login", credentials),
  adminLogin: (credentials) => api.post("/auth/admin/login", credentials),
  getCurrentUser: () => api.get("/auth/me"),
  logout: () => {
    localStorage.removeItem("token")
    return Promise.resolve()
  },
  updateUser: (userData) => api.put(`/users/${userData._id}`, userData),
  submitVerificationDocuments: (userId, documents) => api.post(`/verification/${userId}/submit`, documents),
}

// User service
export const userService = {
  // Get user profile
  getProfile: (userId) => api.get(`/users/${userId}`),

  // Update user profile
  updateProfile: (userId, data) => api.put(`/users/${userId}`, data),

  // Search profiles
  searchProfiles: (params) => api.get("/users/search", { params }),

  // Get featured profiles
  getFeaturedProfiles: () => api.get("/users/featured"),

  // Upload profile image
  uploadProfileImage: (userId, formData) => {
    return api.post(`/users/${userId}/upload-image`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
  },
}

// Connection service
export const connectionService = {
  // Get user connections
  getConnections: () => api.get(`/connections`),

  // Send connection request
  sendRequest: (fromUserId, toUserId) => api.post("/connections/request", { fromUserId, toUserId }),

  // Accept connection request
  acceptRequest: (requestId) => api.put(`/connections/request/${requestId}/accept`),

  // Reject connection request
  rejectRequest: (requestId) => api.put(`/connections/request/${requestId}/reject`),

  // Get pending requests
  getPendingRequests: (userId) => api.get(`/connections/requests/${userId}`),
}

// Message service
export const messageService = {
  // Get conversation messages
  getMessages: (userId, otherUserId) => api.get(`/messages/${userId}/${otherUserId}`),

  // Send message
  sendMessage: (senderId, receiverId, content) =>
    api.post("/messages", { sender: senderId, receiver: receiverId, content }),

  // Mark message as read
  markAsRead: (messageId) => api.put(`/messages/${messageId}/read`),

  // Get recent conversations
  getConversations: (userId) => api.get(`/messages/conversations/${userId}`),
}

// Notification service
export const notificationService = {
  // Get user notifications
  getNotifications: (userId) => api.get(`/notifications/${userId}`),

  // Mark notification as read
  markAsRead: (notificationId) => api.put(`/notifications/${notificationId}/read`),

  // Mark all notifications as read
  markAllAsRead: (userId) => api.put(`/notifications/${userId}/read-all`),
}

// Verification service
export const verificationService = {
  // Submit verification documents
  submitDocuments: (userId, formData) => {
    return api.post(`/verification/${userId}/submit`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
  },

  // Get verification status
  getStatus: (userId) => api.get(`/verification/${userId}/status`),
}

// Payment service
export const paymentService = {
  // Get subscription plans
  getPlans: () => api.get("/payments/plans"),

  // Create order
  createOrder: (userId, planId) => api.post("/payments/create-order", { userId, planId }),

  // Verify payment
  verifyPayment: (paymentData) => api.post("/payments/verify", paymentData),

  // Get user transactions
  getTransactions: (userId) => api.get(`/payments/transactions/${userId}`),
}

// Admin service
export const adminService = {
  // Get dashboard stats
  getDashboardStats: () => api.get("/admin/dashboard/stats"),

  // Get users list
  getUsers: (params) => api.get("/admin/users", { params }),

  // Get user details
  getUserDetails: (userId) => api.get(`/admin/users/${userId}`),

  // Update user status
  updateUserStatus: (userId, status) => api.put(`/admin/users/${userId}/status`, { status }),

  // Get pending verifications
  getPendingVerifications: () => api.get("/admin/verification/pending"),

  // Approve verification
  approveVerification: (verificationId) => api.put(`/admin/verification/${verificationId}/approve`),

  // Reject verification
  rejectVerification: (verificationId, reason) => api.put(`/admin/verification/${verificationId}/reject`, { reason }),

  // Get transactions
  getTransactions: (params) => api.get("/admin/transactions", { params }),
}

export default api
